import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import { Invoices } from "@/components/backOffice/clients/invoices";
import { Parameters } from "@/components/backOffice/clients/parameters";
import { Informations } from "@/components/backOffice/clients/informations/informations";

import { ClientsIdHeader } from "@/components/backOffice/clients/clientsIdHeader";
import { TabNavigation } from "@/components/backOffice/tabNavigation";
import { useGetCompanyBOV1 } from "@/api/company.api";
import { Statistics } from "@/components/backOffice/clients/statistics";
import { useAtomValue } from "jotai";
import { userAtom } from "@/stores/authStore";
import { ClientExpeditions } from "@/components/backOffice/clients/cotationHistory/clientExpeditions";

export const ClientsIdPage = () => {
  const { t } = useTranslation("boClients");
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAtomValue(userAtom);
  const location = useLocation();

  const defaultTab = user?.role === "ACCOUNTANT" ? 1 : 0;
  const tabParam = searchParams.get("tab");
  const activeTab = tabParam != null ? Number(tabParam) : defaultTab;

  const setActiveTab = useCallback(
    (tab: React.SetStateAction<number>) => {
      const newTab = typeof tab === "function" ? tab(activeTab) : tab;
      setSearchParams(
        (prev) => {
          prev.set("tab", newTab.toString());
          return prev;
        },
        { replace: true },
      );
    },
    [activeTab, setSearchParams],
  );

  const companyId = location.pathname.split("/")[location.pathname.split("/").length - 1];

  const { data: company } = useGetCompanyBOV1(companyId, {
    includeBillingEntities: true,
    includePickUpLocations: true,
  });

  const displayTab = () => {
    switch (activeTab) {
      case 0:
        if (!["ADMIN", "SUPERADMIN", "MEMBER"].includes(user?.role || "")) return null;
        return <ClientExpeditions companyId={companyId} />;
      case 1:
        if (!["SUPERADMIN", "ACCOUNTANT"].includes(user?.role || "")) return null;
        return <Invoices companyId={companyId} billingEntities={company?.billingEntities || []} />;
      case 2:
        if (!["SUPERADMIN"].includes(user?.role || "")) return null;
        return <Statistics companyId={companyId} />;
      case 3:
        if (!["SUPERADMIN"].includes(user?.role || "")) return null;
        return <Parameters company={company} />;
      case 4:
        return <Informations companyId={companyId} />;
      default:
        return null;
    }
  };

  const hideNavs = (index: number) => {
    if (!user?.role) return true;
    if (index === 1 && !["SUPERADMIN", "ACCOUNTANT"].includes(user?.role)) return true;
    if (index === 0 && !["ADMIN", "SUPERADMIN", "MEMBER"].includes(user?.role)) return true;
    if ((index === 2 || index === 3) && !["SUPERADMIN"].includes(user?.role)) return true;

    return false;
  };

  return (
    <div className="flex h-fit w-full flex-1 flex-col gap-8 p-8">
      <div className="flex flex-col gap-6">
        <ClientsIdHeader companyName={company?.businessName || ""} />
        <div className="flex flex-1 flex-row border-b border-gray-200">
          <TabNavigation
            hideNavs={hideNavs}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            texts={[
              t("clientPage.tabs.expeditions"),
              t("clientPage.tabs.invoices"),
              t("clientPage.tabs.statistics"),
              t("clientPage.tabs.parameters"),
              t("clientPage.tabs.informations"),
            ]}
          />
        </div>
      </div>
      {displayTab()}
    </div>
  );
};
